package com.shadowings.gero.presentation.screen.characters

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.Button
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.unit.dp
import com.shadowings.gero.domain.model.Faces
import com.shadowings.gero.domain.model.GameConstants
import com.shadowings.gero.domain.model.UserData
import com.shadowings.gero.presentation.component.avatars.MaskoAvatar
import com.shadowings.gero.presentation.ui.AppTheme
import com.shadowings.gero.presentation.util.DeviceClass
import com.shadowings.gero.presentation.util.rememberDeviceInfo
import compose.icons.FeatherIcons
import compose.icons.feathericons.Lock
import gero.composeapp.generated.resources.Res
import gero.composeapp.generated.resources.builder_pro_unlock_btn
import gero.composeapp.generated.resources.chiudi_btn
import gero.composeapp.generated.resources.custom_hat_locked_caption
import gero.composeapp.generated.resources.custom_mask_locked_desc
import gero.composeapp.generated.resources.custom_mask_locked_title
import gero.composeapp.generated.resources.custom_mask_type_base
import gero.composeapp.generated.resources.custom_mask_type_einstein
import gero.composeapp.generated.resources.custom_mask_type_pirata
import gero.composeapp.generated.resources.custom_mask_type_riccone
import gero.composeapp.generated.resources.custom_title_bg
import gero.composeapp.generated.resources.custom_title_body
import gero.composeapp.generated.resources.custom_title_eyebrows
import gero.composeapp.generated.resources.custom_title_eyes
import gero.composeapp.generated.resources.custom_title_face
import gero.composeapp.generated.resources.custom_title_glasses
import gero.composeapp.generated.resources.custom_title_mask_type
import gero.composeapp.generated.resources.custom_title_nose
import gero.composeapp.generated.resources.custom_title_shirt
import gero.composeapp.generated.resources.custom_title_whiskers
import gero.composeapp.generated.resources.masko_base
import gero.composeapp.generated.resources.masko_einstein
import gero.composeapp.generated.resources.pirate
import gero.composeapp.generated.resources.rich
import org.jetbrains.compose.resources.stringResource

@Preview
@Preview(name = "Dark", uiMode = 0x20, showBackground = true)
@Composable
private fun MaskoCharacterScreenPreview() {
    AppTheme {
        Surface {
            MaskoCharacterScreen(
                userData = UserData(
                    maskoColors = mapOf(
                        "bg" to 9,
                        "skin" to 0,
                        "glasses" to 9,
                        "shirt" to 7,
                        "baffi" to 9,
                        "eyebrows" to 9,
                        "eyes" to 1,
                        "naso" to 0,
                    )
                ),
                onMaskoColorChanged = { _, _ -> }
            )
        }
    }
}

/** level=0 (below every mask requirement): shows the picker with locks + captions. */
@Preview(name = "Masks locked")
@Composable
private fun MaskoCharacterScreenMasksLockedPreview() {
    AppTheme {
        Surface {
            MaskoCharacterScreen(
                userData = UserData(maskoColors = mapOf(), experience = 0),
                onMaskoColorChanged = { _, _ -> }
            )
        }
    }
}

@Composable
fun MaskoCharacterScreen(
    userData: UserData,
    onMaskoColorChanged: (categoryKey: String, index: Int) -> Unit,
    onResetClick: () -> Unit = {},
    onPurchaseFullVersion: () -> Unit = {},
    modifier: Modifier = Modifier,
) {
    val bgColorIndex =
        (userData.maskoColors[CharacterConsts.BG_KEY] ?: 9).coerceIn(0, backgroundOptions.size - 1)
    val skinColorIndex = (userData.maskoColors["skin"] ?: 0).coerceIn(0, shirtOptions.size - 1)
    val glassesIndex = (userData.maskoColors["glasses"] ?: 9).coerceIn(0, hairOptions.size - 1)
    val shirtColorIndex = (userData.maskoColors["shirt"] ?: 7).coerceIn(0, shirtOptions.size - 1)
    val baffiIndex = (userData.maskoColors["baffi"] ?: 9).coerceIn(0, hairOptions.size - 1)
    val eyebrowsIndex = (userData.maskoColors["eyebrows"] ?: 9).coerceIn(0, hairOptions.size - 1)
    val eyesColorIndex = (userData.maskoColors["eyes"] ?: 1).coerceIn(0, eyesOptions.size - 1)
    val nasoColorIndex = (userData.maskoColors["naso"] ?: 0).coerceIn(0, skinOptions.size - 1)
    val maskTypeIndex = (userData.maskoColors[CharacterConsts.MASK_TYPE_KEY] ?: 0).coerceIn(0, 3)

    // livello minimo richiesto per ogni maschera, in ordine di opzione (0 = nessun requisito)
    val maskTypeLevelRequirements = listOf(0, 22, 30, 40)
    val userLevel = GameConstants.getPlayerLevel(userData.experience)
    var lockedMaskRequiredLevel by remember { mutableStateOf<Int?>(null) }

    val selectedFace = remember { mutableStateOf(Faces.Idle) }

    val deviceInfo = rememberDeviceInfo()
    val isPhoneLargePortrait =
        deviceInfo.deviceClass == DeviceClass.PHONE_LARGE && !deviceInfo.isLandscape
    val avatarSize = when (deviceInfo.deviceClass) {
        DeviceClass.PHONE_SMALL -> 64.dp
        DeviceClass.PHONE_MEDIUM -> 80.dp
        DeviceClass.PHONE_LARGE -> if (isPhoneLargePortrait) 120.dp else 110.dp
        DeviceClass.TABLET_MEDIUM -> 120.dp
        DeviceClass.TABLET_LARGE -> 150.dp
        DeviceClass.WEB_FULL -> 150.dp
    }

    Column(
        modifier = modifier
            .fillMaxSize()
            .padding(horizontal = 16.dp),
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        Spacer(Modifier.size(16.dp))

        Row(
            modifier = Modifier.fillMaxWidth(),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Box(
                Modifier.background(
                    color = backgroundOptions[bgColorIndex],
                    shape = RoundedCornerShape(6.dp)
                ).padding(top = 6.dp, start = 6.dp, end = 6.dp)
            ) {
                val avatarModifier = Modifier
                    .height(avatarSize)
                    .align(Alignment.BottomCenter)
                MaskoAvatar(
                    userData = userData,
                    face = selectedFace.value,
                    modifier = avatarModifier
                )
            }

            Spacer(Modifier.width(16.dp))

            Column(modifier = Modifier.weight(1f)) {
                Text(
                    text = stringResource(Res.string.custom_title_face),
                    style = MaterialTheme.typography.titleMedium
                )
                Spacer(Modifier.height(6.dp))
                FaceMenu(
                    selectedFace = selectedFace.value,
                    onFaceSelected = { selectedFace.value = it },
                    faces = listOf(Faces.Idle, Faces.Surprised, Faces.Sad)
                )
            }
        }

        Spacer(Modifier.size(20.dp))

        // (glassesColorIndexSnap, accentColorIndexOrNull, baffiColorIndexSnap) per option in
        // order base/pirata/riccone/einstein — snapped on select to a sensible starting tone for
        // glasses, the mask's new accent layer (hat/hat/hair), and the mustache.
        val maskTypeBaseColors = listOf(
            Triple(9, null, 9),
            Triple(9, 7, 0),
            Triple(2, 9, 9),
            Triple(9, 14, 14)
        )

        val maskTypeOptionsRaw = listOf(
            Res.drawable.masko_base to stringResource(Res.string.custom_mask_type_base),
            Res.drawable.pirate to stringResource(Res.string.custom_mask_type_pirata),
            Res.drawable.rich to stringResource(Res.string.custom_mask_type_riccone),
            Res.drawable.masko_einstein to stringResource(Res.string.custom_mask_type_einstein),
        )
        val maskTypeOptions = maskTypeOptionsRaw.mapIndexed { index, (drawable, description) ->
            val requiredLevel = maskTypeLevelRequirements[index]
            val locked =
                requiredLevel > 0 && userLevel < requiredLevel && !userData.isFullVersionUnlocked
            HatTypeOptionData(
                drawable = drawable,
                contentDescription = description,
                locked = locked,
                lockCaption = if (locked) {
                    stringResource(Res.string.custom_hat_locked_caption, requiredLevel)
                } else {
                    null
                }
            )
        }

        val maskTypeSectionContent: @Composable () -> Unit = {
            HatTypeSection(
                title = stringResource(Res.string.custom_title_mask_type),
                options = maskTypeOptions,
                selectedIndex = maskTypeIndex,
                onSelect = { index ->
                    onMaskoColorChanged(CharacterConsts.MASK_TYPE_KEY, index)
                    val (glassesColor, accentColor, baffiColor) = maskTypeBaseColors[index]
                    onMaskoColorChanged("glasses", glassesColor)
                    if (accentColor != null) onMaskoColorChanged(
                        CharacterConsts.MASK_ACCENT_KEY,
                        accentColor
                    )
                    onMaskoColorChanged("baffi", baffiColor)
                },
                onLockedClick = { index ->
                    lockedMaskRequiredLevel = maskTypeLevelRequirements[index]
                }
            )
        }

        val sections = listOf(
            ColorSectionData(
                title = stringResource(Res.string.custom_title_bg),
                options = backgroundOptions,
                selectedIndex = bgColorIndex,
                onSelect = { onMaskoColorChanged(CharacterConsts.BG_KEY, it) }
            ),
            ColorSectionData(
                title = stringResource(Res.string.custom_title_body),
                options = shirtOptions,
                selectedIndex = skinColorIndex,
                onSelect = { onMaskoColorChanged("skin", it) }
            ),
            ColorSectionData(
                title = stringResource(Res.string.custom_title_shirt),
                options = shirtOptions,
                selectedIndex = shirtColorIndex,
                onSelect = { onMaskoColorChanged("shirt", it) }
            ),
            ColorSectionData(
                title = stringResource(Res.string.custom_title_eyes),
                options = eyesOptions,
                selectedIndex = eyesColorIndex,
                onSelect = { onMaskoColorChanged("eyes", it) }
            ),
            ColorSectionData(
                title = stringResource(Res.string.custom_title_glasses),
                options = hairOptions,
                selectedIndex = glassesIndex,
                onSelect = { onMaskoColorChanged("glasses", it) }
            ),
            ColorSectionData(
                title = stringResource(Res.string.custom_title_whiskers),
                options = hairOptions,
                selectedIndex = baffiIndex,
                onSelect = { onMaskoColorChanged("baffi", it) }
            ),
            ColorSectionData(
                title = stringResource(Res.string.custom_title_eyebrows),
                options = hairOptions,
                selectedIndex = eyebrowsIndex,
                onSelect = { onMaskoColorChanged("eyebrows", it) }
            ),
            ColorSectionData(
                title = stringResource(Res.string.custom_title_nose),
                options = skinOptions,
                selectedIndex = nasoColorIndex,
                onSelect = { onMaskoColorChanged("naso", it) }
            ),
        )
        ColorsPicker(
            sections = sections,
            onResetClick = onResetClick,
            headerContent = maskTypeSectionContent
        )
    }

    val requiredLevel = lockedMaskRequiredLevel
    if (requiredLevel != null) {
        AlertDialog(
            onDismissRequest = { lockedMaskRequiredLevel = null },
            icon = {
                Icon(
                    imageVector = FeatherIcons.Lock,
                    contentDescription = null,
                    tint = MaterialTheme.colorScheme.primary,
                    modifier = Modifier.size(28.dp)
                )
            },
            title = {
                Text(
                    text = stringResource(Res.string.custom_mask_locked_title),
                    fontWeight = FontWeight.Bold,
                    textAlign = TextAlign.Center
                )
            },
            text = {
                Text(
                    text = stringResource(Res.string.custom_mask_locked_desc, requiredLevel),
                    textAlign = TextAlign.Center
                )
            },
            confirmButton = {
                Button(
                    onClick = {
                        lockedMaskRequiredLevel = null
                        onPurchaseFullVersion()
                    }
                ) {
                    Text(stringResource(Res.string.builder_pro_unlock_btn))
                }
            },
            dismissButton = {
                TextButton(onClick = { lockedMaskRequiredLevel = null }) {
                    Text(stringResource(Res.string.chiudi_btn))
                }
            }
        )
    }
}
